import { FC } from 'react';
import { defineMessage, MessageDescriptor } from 'react-intl';
import appleStoreStars from 'images/apple-store-stars@2x.png';
import appleStoreLight from 'images/apple-store_light@2x.png';
import googlePlayStars from 'images/google-play-stars@2x.png';
import googlePlayLight from 'images/google-play_light@2x.png';
import { useIsClientSideSSR } from 'src/hooks/useStateSSR';
import { RequiredExternalHrefParams } from 'src/utils/getExternalHref';
import LinkToAppStore from 'src/containers/LinkToAppStore';
import LinkToGooglePlay from 'src/containers/LinkToGooglePlay';

export type MobileAppStoresDisplayType = 'all' | 'preferred';

export type StoreType = 'app-store' | 'google-play';

export type MobileAppStoreData = {
  storeType: StoreType;
  storeImages: {
    light: string;
  };
  storeImageAlt: MessageDescriptor;
  starsImageSrc: string;
  starsImageAlt: MessageDescriptor;
  ratingMark: MessageDescriptor;
  ratingsCount: MessageDescriptor;
  hrefParams: RequiredExternalHrefParams;
  DownloadButton: FC;
};

const appleStoreData: MobileAppStoreData = {
  storeType: 'app-store',
  storeImages: {
    light: appleStoreLight,
  },
  storeImageAlt: defineMessage({
    id: 'download_section_apple_store_logo',
  }),
  starsImageSrc: appleStoreStars,
  starsImageAlt: defineMessage({
    id: 'download_section_apple_store_stars_rate',
  }),
  ratingMark: defineMessage({ id: 'download_section_apple_store_rating' }),
  ratingsCount: defineMessage({
    id: 'download_section_apple_store_reviews',
  }),
  hrefParams: {
    linkType: 'memrise-app-store',
  },
  DownloadButton: LinkToAppStore,
};

const googlePlayData: MobileAppStoreData = {
  storeType: 'google-play',
  storeImages: {
    light: googlePlayLight,
  },
  storeImageAlt: defineMessage({
    id: 'download_section_google_play_logo',
  }),
  starsImageSrc: googlePlayStars,
  starsImageAlt: defineMessage({
    id: 'download_section_google_play_stars_rate',
  }),
  ratingMark: defineMessage({ id: 'download_section_google_play_rating' }),
  ratingsCount: defineMessage({
    id: 'download_section_google_play_reviews',
  }),
  hrefParams: {
    linkType: 'memrise-google-play',
  },
  DownloadButton: LinkToGooglePlay,
};

const ALL_STORES = [appleStoreData, googlePlayData];

export const useMobileAppStores = (displayType: MobileAppStoresDisplayType) => {
  const isClient = useIsClientSideSSR();

  const isAndroid = isClient && window.navigator.userAgent.match(/Android/i);

  const isIos = isClient && window.navigator.userAgent.match(/iPhone|iPad|iPod/i);

  if (displayType === 'all') return ALL_STORES;

  if (isAndroid) return [googlePlayData];

  if (isIos) return [appleStoreData];

  return ALL_STORES;
};
